<script setup>
useSeoMeta({
  title: '선물함',
  robots: 'noindex, nofollow',
});

import { useStore } from 'vuex';
import { jwtDecode } from 'jwt-decode';
import GiftList from '@/components/purchase/GiftList.vue';
import Pagination from '@/components/board/Pagination.vue';
import PopGiftView from '@/components/purchase/PopGiftView.vue';
import PopCouponView from '@/components/purchase/PopCouponView.vue';
import IconHeaderClose from '@/components/svg/IconHeaderClose.vue';
import MypageLnbTabMenu from '@/components/MypageLnbTabMenu.vue';
import { acceptGift, getGiftInbox, getGiftOutbox, validateGiftCode } from '@/api/gifts';
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import { useDialogManager } from '@/composables/useDialogManager';

import { useRnBridge } from '@/composables/useRnBridge';
import { useProductThumbnail } from '@/composables/useProductThumbnail';
import { gnbMenuLinks } from '@/constants/menuLinks';
import { gnbContentTypeMap as contentTypeMap } from '@/constants/logContentType';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import DropdownCreditInfo from '@/components/common/DropdownCreditInfo.vue';

definePage({ meta: { layout: 'purchase', title: '서비스구매' } });

const creditInfoRef = ref(null);

const store = useStore();
const myInfoStore = useMyInfoStore();
const router = useRouter();
const route = useRoute();
const { sendMessageToApp } = useRnBridge();
const { ensureThumbnailMap } = useProductThumbnail();
const {
  dialogTitle,
  dialogTitle2,
  dialogText,
  dialogWarning,
  dialogNextUrl,
  alertRef,
  confirmRef,
  showAlert,
  showConfirm,
} = useDialogManager();

const isWebView = ref(false);
const userId = computed(() => store.getters['user/id']);
const wideLayout = ref(window.innerWidth >= 768);

// 쿼리에서 탭/페이지/선물ID 복원
const validTabs = ['inbox', 'outbox', 'coupon'];
const initialTab = validTabs.includes(route.query.tab) ? route.query.tab : 'inbox';
const initialPage = parseInt(route.query.page) || 1;
const initialGiftId = route.query.id ? parseInt(route.query.id) : null;

const tabType = ref(initialTab);
const giftList = ref([]);
const isLoading = ref(true);
const totalElements = ref(0);
const totalPages = ref(0);

const fetchParams = ref({
  page: initialPage,
  size: window.ReactNativeWebView ? 10 : 9,
});

const giftViewRef = ref(null);
const couponViewRef = ref(null);
const selectedGift = ref(null);
const hasOpenedInitialGift = ref(false);

const isCouponGift = (info) =>
  info?.grantType === 'COUPON_UNIQUE' || info?.grantType === 'COUPON_SHARED';

const openGiftDetail = (info) => {
  selectedGift.value = info;
  if (isCouponGift(info)) {
    couponViewRef.value?.open();
  } else {
    giftViewRef.value?.open();
  }
};

// ========================= 쿠폰 관련 =========================
const couponCode = ref('');
const couponRegisterLoading = ref(false);
const couponConfirmRef = ref(null);
const loginConfirmRef = ref(null);

const onClickCouponRegister = () => {
  if (!isGiftLoggedIn.value) {
    loginConfirmRef.value?.openConfirmDialog();
    return;
  }
  if (!couponCode.value.trim()) return;
  couponConfirmRef.value?.openConfirmDialog();
};

const handleCouponRegister = async () => {
  if (couponRegisterLoading.value) return;

  couponRegisterLoading.value = true;
  try {
    const code = couponCode.value.trim();
    const giftInfo = await validateGiftCode(code);

    if (giftInfo.grantType?.startsWith('GIFT')) {
      showAlert({ title: '유효하지 않은 쿠폰입니다.' });
      return;
    }

    await acceptGift(code);
    couponCode.value = '';
    changeTab('inbox');
    showAlert({ title: '등록되었습니다.' });
  } catch (e) {
    const errorMessage = e.response?.data?.detail || '쿠폰 등록에 실패했습니다.';
    showAlert({ title: errorMessage });
  } finally {
    couponRegisterLoading.value = false;
  }
};

const isGiftLoggedIn = computed(() => !!userId.value || !!sessionStorage.getItem('X-Token'));

/** 화면 너비에 따라 wideLayout 설정 */
const updateLayout = () => {
  wideLayout.value = window.innerWidth >= 768;
};

/** 선물 목록 조회 */
const fetchGiftList = async () => {
  // 로그인 안 된 경우 로딩 해제
  if (!isGiftLoggedIn.value) {
    isLoading.value = false;
    giftList.value = [];
    totalElements.value = 0;
    totalPages.value = 0;
    return;
  }

  isLoading.value = true;
  try {
    // 썸네일 매핑 테이블 초기화 (캐시 있으면 스킵)
    await ensureThumbnailMap();

    const result =
      tabType.value === 'inbox'
        ? await getGiftInbox(fetchParams.value)
        : await getGiftOutbox(fetchParams.value);

    giftList.value = result.content || [];
    totalElements.value = result.totalElements || 0;
    totalPages.value = result.totalPages || 0;
  } catch (error) {
    console.error('선물 목록 조회 실패:', error);
  } finally {
    isLoading.value = false;
  }
};

/** 탭 변경 */
const changeTab = (type) => {
  if (tabType.value === type) return;
  tabType.value = type;
  if (type === 'coupon') return;
  fetchParams.value.page = 1;
  fetchGiftList();
};

/** 페이지 변경 */
const changePage = (page) => {
  fetchParams.value.page = page;
  fetchGiftList();
};

/** 쿼리의 giftId로 선물 조회 후 팝업 열기 (앱에서만 동작) */
const openGiftPopupById = async () => {
  if (!window.ReactNativeWebView || !initialGiftId || hasOpenedInitialGift.value) return;

  try {
    hasOpenedInitialGift.value = true;
    const result = await validateGiftCode(initialGiftId);
    // 만료된 선물은 팝업 열지 않음
    if (result?.status === 'EXPIRED') return;
    openGiftDetail(result);
  } catch (error) {
    console.error('선물 조회 실패:', error);
  }
};

/** 선물 아이템 클릭 */
const handleGiftClick = async (item) => {
  // 보낸 선물함 또는 수락된 선물: 팝업으로 상세 보기
  if (tabType.value === 'outbox' || item.status === 'ACCEPTED') {
    try {
      const result = await validateGiftCode(item.code);
      openGiftDetail({ ...result, serialCode: item.code });
    } catch (error) {
      console.error('선물 정보 조회 실패:', error);
    }
    return;
  }

  // 받은 선물함의 대기중인 선물: 수락 페이지로 이동
  router.push({
    path: '/purchase/gift-claim',
    query: { code: item.code },
  });
};

const openTicketTab = () => {
  router.push('/purchase/tickets');
};

const openPointTab = () => {
  router.push('/purchase/points');
};

const handleClose = () => {
  router.push('/main');
};

// --- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) ---
const isMypageMenuOpen = ref(false);

const toggleMypageMenu = () => {
  isMypageMenuOpen.value = !isMypageMenuOpen.value;
};

const closeMypageMenu = () => {
  isMypageMenuOpen.value = false;
};

watch(
  () => route.path,
  () => {
    if (isMypageMenuOpen.value) {
      isMypageMenuOpen.value = false;
    }
  },
);

const isTokenValid = () => {
  const token = sessionStorage.getItem('X-Token');
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

const isLoggedIn = computed(() => {
  const userName = store.getters['user/name'];
  try {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || 'null');
    return !!(userId.value || userName || userInfo?.name);
  } catch {
    return !!(userId.value || userName);
  }
});

/** 로그인 확인 후 로그인 팝업 표시 */
const confirmLoginForGiftBox = () => {
  if (isWebView.value) {
    sendMessageToApp({ action: 'login' });
    return;
  }

  router.push({
    path: '/auth/login',
    query: { to: route.fullPath },
  });
};

const showLoginRequiredPopup = () => {
  showConfirm({
    title: '로그인 후 이용 가능합니다.',
    title2: '로그인 페이지로 이동하시겠습니까?',
    text: '확인',
    onConfirm: () => confirmLoginForGiftBox(),
    onCancel: () => router.push('/purchase/tickets'),
  });
};

const displayName = computed(() => {
  if (isLoggedIn.value) {
    return myInfoStore?.displayName || store.getters['user/name'] || '';
  }
  return '';
});

const isHomeRoute = computed(() => route.path === '/main' || route.path === '/');

const handleGoHome = () => {
  closeMypageMenu();
  if (!isHomeRoute.value) {
    router.push('/main');
  }
};

const handleLogin = () => {
  if (isWebView.value) {
    sendMessageToApp({ action: 'login' });
    return;
  }

  addContentLogAPI('gnb_btn_join');
  router.push({
    path: '/auth/login',
    query: { to: route.fullPath },
  });
  closeMypageMenu();
};

const isMenuActive = (menuLink) => {
  const currentPath = route.path;
  switch (menuLink.name) {
    case '낭만분석':
      return (
        currentPath.startsWith('/saju-service') ||
        currentPath.startsWith('/saju-result') ||
        currentPath.startsWith('/saju-insight') ||
        currentPath.startsWith('/analysis')
      );
    default:
      return currentPath.startsWith(menuLink.basePath || menuLink.link);
  }
};

const handleMenuClick = (menu) => {
  addContentLogAPI(contentTypeMap[menu.link]);
  if (menu.name !== '낭만만세력은?') {
    router.push(menu.link);
  }
  closeMypageMenu();
};

const handleLogout = async () => {
  addContentLogAPI('pi_btn_logout');
  try {
    await store.dispatch('user/logout');
    clearMySajuState();
    await router.push('/auth/login');
    myInfoStore.resetSelectedOption();
    closeMypageMenu();
  } catch (error) {
    console.error('Logout error:', error);
  }
};

onMounted(async () => {
  isWebView.value = !!window.ReactNativeWebView;

  if (tabType.value === 'coupon') {
    const serial = route.query.serial;
    if (serial) couponCode.value = serial;
  } else {
    await fetchGiftList();
    openGiftPopupById();
  }

  window.addEventListener('resize', updateLayout);
});

// 같은 페이지에서 query 변경 시 (딥링크로 탭/시리얼 변경)
watch(
  () => route.query,
  (query) => {
    const tab = validTabs.includes(query.tab) ? query.tab : null;
    if (tab && tab !== tabType.value) {
      changeTab(tab);
      if (tab === 'coupon' && query.serial) {
        couponCode.value = query.serial;
      }
    }
  },
);

onUnmounted(() => {
  window.removeEventListener('resize', updateLayout);
});

watch(
  () => isGiftLoggedIn.value,
  async (loggedIn) => {
    if (loggedIn) {
      await fetchGiftList();
      return;
    }

    isLoading.value = false;
    giftList.value = [];
    totalElements.value = 0;
    totalPages.value = 0;
  },
);
</script>

<template>
  <div class="my-info-wrap gift-box-wrap">
    <div class="my-tit-box com-bg-my-box h-small st-guide com-pc-title-box">
      <div class="tit">
        선물함
        <div class="tit-s">선물을 주고 받은 내역을 확인할 수 있어요.</div>
      </div>
    </div>

    <div
      class="com-app-tit-box purchase-service-header"
      :class="{ 'purchase-service-header--app': isWebView }"
    >
      <div class="tit">서비스 구매</div>
      <div class="btn-right">
        <button v-if="isWebView" class="btn-back" @click="handleClose">
          <IconHeaderClose />
        </button>
        <button v-else class="btn-mypage" @click="toggleMypageMenu">
          <i class="icon-mypage"></i>
        </button>
      </div>
    </div>

    <div class="com-app-tab-box purchase-service-tabs">
      <button class="tab-button" @click="openTicketTab">분석권 구매</button>
      <button class="tab-button" @click="openPointTab">포인트 충전</button>
      <button class="tab-button selected">선물함</button>
    </div>

    <div class="myinfo-box com-bg-my-box product">
      <div class="category-box">
        <div class="category-box__tabs">
          <button
            class="category-box__tab"
            :class="{ active: tabType === 'inbox' }"
            @click="changeTab('inbox')"
          >
            받은 선물
          </button>
          <button
            class="category-box__tab"
            :class="{ active: tabType === 'outbox' }"
            @click="changeTab('outbox')"
          >
            보낸 선물
          </button>
          <button
            class="category-box__tab"
            :class="{ active: tabType === 'coupon' }"
            @click="changeTab('coupon')"
          >
            쿠폰 등록
          </button>
        </div>
        <div v-if="tabType === 'inbox' || tabType === 'outbox'" class="category-box__right">
          <span class="category-box__count">총 {{ totalElements }}개</span>
        </div>
      </div>

      <!-- 쿠폰 등록 탭 -->
      <div v-if="tabType === 'coupon'" class="coupon-content">
        <div class="coupon-content__card">
          <div class="coupon-content__form">
            <label class="coupon-content__label">쿠폰 등록하기</label>
            <input
              v-model="couponCode"
              type="text"
              class="coupon-content__input"
              placeholder="쿠폰 코드를 입력하세요"
            />
            <button
              class="coupon-content__button"
              :class="{ active: couponCode.trim() && !couponRegisterLoading }"
              :disabled="!couponCode.trim() || couponRegisterLoading"
              @click="onClickCouponRegister"
            >
              {{ couponRegisterLoading ? '등록 중...' : '쿠폰 등록하기' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 선물 탭 -->
      <div v-else-if="isLoading" class="gift-loading-box">
        <div class="loading-spinner"></div>
      </div>

      <template v-else-if="!isLoading && giftList.length > 0">
        <div class="gift-list-box">
          <GiftList
            :items="giftList"
            :wide-layout="wideLayout"
            :type="tabType"
            @click-item="handleGiftClick"
          />
          <Pagination
            v-if="totalPages > 1"
            :total-pages="totalPages"
            :current-page="fetchParams.page"
            @page-changed="changePage"
          />
        </div>
      </template>

      <div v-else class="gift-none-box">
        <div class="txt1">
          {{ tabType === 'outbox' ? '보낸 선물이 없습니다.' : '받은 선물이 없습니다.' }}
        </div>
        <button v-if="tabType === 'outbox'" class="gift-none-box__action" @click="openTicketTab">
          선물하러 가기
        </button>
        <button
          v-if="tabType === 'inbox' && !isGiftLoggedIn"
          class="gift-none-box__action"
          @click="handleLogin"
        >
          로그인 후 이용하기
        </button>
      </div>
    </div>

    <PopGiftView ref="giftViewRef" :gift-info="selectedGift" :type="tabType" />
    <PopCouponView ref="couponViewRef" :gift-info="selectedGift" />
    <AlertComponent
      ref="alertRef"
      :title="dialogTitle"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
    />
    <ConfirmComponent
      ref="couponConfirmRef"
      title="쿠폰번호를 등록하시겠습니까?"
      title2="등록된 쿠폰은 취소가 불가능합니다."
      text="등록"
      :next-url="handleCouponRegister"
    />
    <ConfirmComponent
      ref="loginConfirmRef"
      title="로그인 후 이용 가능합니다."
      text="로그인"
      :next-url="handleLogin"
    />

    <AlertComponent ref="alertRef" :title="dialogTitle" :text="dialogText" />
    <ConfirmComponent
      ref="confirmRef"
      :title="dialogTitle"
      :title2="dialogTitle2"
      :text="dialogText"
      :warning="dialogWarning"
      :nextUrl="dialogNextUrl"
    />

    <Teleport to="body">
      <div
        v-if="isMypageMenuOpen && !isWebView"
        class="mypage-menu-overlay"
        @click="closeMypageMenu"
      >
        <div class="mypage-menu-dropdown" @click.stop>
          <div class="login-msg-box">
            <button class="btn-home" :class="{ active: isHomeRoute }" @click="handleGoHome">
              <i class="icon-home"></i>
              홈
            </button>
            <template v-if="isLoggedIn">
              <div class="txt-box">
                <span
                  ><span class="txt1" style="margin-right: 2px">{{ displayName || '게스트' }}</span
                  >님</span
                >
                <span class="txt2">행복한 하루 되세요</span>
              </div>
            </template>
            <template v-else>
              <div class="txt-box">
                <span class="txt1">게스트</span>
                <span class="txt2">로그인 후 이용해주세요</span>
              </div>
            </template>

            <DropdownCreditInfo
              v-if="isLoggedIn"
              ref="creditInfoRef"
              :is-logged-in="isLoggedIn"
              :visible="isMypageMenuOpen"
              @close-menu="closeMypageMenu"
            />

            <div class="gnb-menu-list">
              <template v-for="(menu, key) in gnbMenuLinks" :key="key">
                <router-link
                  v-if="menu.name !== '낭만만세력은?'"
                  :to="menu.link"
                  :class="{ active: isMenuActive(menu) }"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                >
                  {{ menu.name }}
                </router-link>
                <a
                  v-else
                  :href="$router.resolve(menu.link).href"
                  target="_blank"
                  rel="noopener"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                >
                  {{ menu.name }}
                </a>
              </template>
            </div>

            <template v-if="isLoggedIn">
              <MypageLnbTabMenu />
              <button class="btn-xs line" @click="handleLogout">로그아웃</button>
            </template>
            <template v-else>
              <button class="btn-xs line login-btn" @click="handleLogin">로그인</button>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.gift-box-wrap {
  background: #fff;
  word-break: keep-all;

  .my-tit-box {
    margin: 0 0 28px;
    padding: 40px 0 0;

    .tit {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 20px;
      margin: 0;
      font-size: 20px;
      line-height: 24px;
    }

    .tit-s {
      margin-left: 0;
    }
  }

  .myinfo-box.com-bg-my-box.product {
    padding: 0;
  }

  .category-box {
    padding: 0 20px 20px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__tabs {
      display: flex;
      gap: 10px;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__tab {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      white-space: nowrap;
      padding: 6px 15px;
      border-radius: 16px;
      border: 1px solid #eb4c10;
      color: #eb4c10;
      font-size: 15px;
      font-weight: 700;
      line-height: 18px;

      &.active {
        background: #eb4c10;
        color: #fff;
      }
    }

    &__right {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: 8px;
      margin-left: 12px;
    }

    &__count {
      color: #333;
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
      white-space: nowrap;
    }
  }

  .gift-none-box {
    width: 100%;
    height: 100%;
    padding: 200px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    &__action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 6px 10px;
      border-radius: 6px;
      border: 1px solid #eb4c10;
      background: #fff;
      color: #eb4c10;
      font-size: 12px;
      font-weight: 700;
      line-height: 14px;
    }
  }

  .gift-loading-box {
    width: 100%;
    padding: 100px 0;
    display: flex;
    justify-content: center;
    align-items: center;

    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid #e5e5e5;
      border-top-color: #eb4c10;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .coupon-content {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 0;

    &__card {
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 400px;
      width: 100%;
      padding: 32px;
      background: #fff;
      border-radius: 24px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    }

    &__form {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    &__label {
      color: #333;
      font-size: 16px;
      font-weight: 700;
      line-height: 130%;
      margin-bottom: 4px;
    }

    &__input {
      width: 100%;
      height: 48px;
      padding: 0 16px;
      border: 1px solid #e5e5e5;
      border-radius: 8px;
      font-size: 16px;
      font-family: Pretendard, sans-serif;
      outline: none;
      box-sizing: border-box;
      transition: border-color 0.2s;

      &:focus {
        border-color: #eb4c10;
      }

      &::placeholder {
        color: #949494;
      }
    }

    &__button {
      width: 100%;
      height: 48px;
      border: none;
      border-radius: 8px;
      background: #949494;
      color: #fff;
      font-size: 15px;
      font-weight: 700;
      font-family: Pretendard, sans-serif;
      cursor: not-allowed;
      transition: all 0.2s;

      &.active {
        background: #eb4c10;
        cursor: pointer;

        &:hover {
          background: #d44410;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .gift-box-wrap {
    position: relative;
    width: 100vw;
    display: flex;
    flex-direction: column;
    margin-bottom: 0;

    .myinfo-box.com-bg-my-box.product {
      margin-top: 0;
      border-radius: 0;
      background: #fff;
    }

    .gift-list-box {
      padding: 12px 16px calc(var(--safe-area-bottom) + 32px);

      .pagination {
        margin-bottom: 0;
        padding-bottom: 0;
      }
    }

    .category-box {
      padding: 10px 16px 8px;
      gap: 6px;
      background: #fff;
      justify-content: space-between;

      &__tabs {
        gap: 6px;
      }

      &__tab {
        margin-right: 0;
        padding: 6px 12px;
        height: 30px;
        box-sizing: border-box;
        border-radius: 16px;
        border: 1px solid transparent;
        background: #f8f8f8;
        color: #949494;
        font-size: 14px;
        font-weight: 500;
        line-height: 18px;

        &.active {
          border-color: #eb4c10;
          background: #eb4c10;
          color: #fff;
          font-weight: 700;
        }
      }

      &__right {
        display: flex;
      }

      &__count {
        color: #333;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
      }
    }

    .gift-loading-box,
    .gift-none-box {
      min-height: calc(100vh - 220px);
      padding: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
    }

    .coupon-content {
      padding: 0;

      &__card {
        max-width: 100%;
        padding: 16px 16px;
        border-radius: 0;
        box-shadow: none;
      }

      &__form {
        gap: 8px;
      }
    }

    .gift-none-box {
      .txt1 {
        color: #757575;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
      }

      &__action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 6px 10px;
        border-radius: 6px;
        border: 1px solid #eb4c10;
        background: #fff;
        color: #eb4c10;
        font-size: 12px;
        font-weight: 700;
        line-height: 14px;
      }
    }

    :deep(.gift-list) {
      margin: 0;
      gap: 20px;

      .gift-card {
        flex: 0 0 100%;
        max-width: 100%;
      }

      .gift-card__thumbnail img {
        object-fit: cover;
      }

      .gift-card__content {
        padding: 20px 19px;
      }

      .gift-card__title {
        font-size: 18px;
      }

      .gift-card__subtitle {
        font-size: 13px;
        line-height: 18px;
      }
    }
  }

  .purchase-service-header {
    justify-content: center;
    border-bottom: 1px solid #ebebeb;

    .btn-right {
      position: absolute;
      right: 8px;
      display: flex;
      align-items: center;
      gap: 0;
    }

    .btn-right .btn-back {
      display: inline-flex;
      width: 24px;
      height: 24px;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;
      padding: 0;
      cursor: pointer;
    }

    .btn-right .btn-mypage {
      display: inline-flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      padding: 0;
      cursor: pointer;
    }

    .btn-right .btn-mypage .icon-mypage {
      display: flex;
      width: 32px;
      height: 32px;
      border-radius: 100%;
      background: url('@manseImages/icon-mypage.png') center center no-repeat;
      background-size: 32px auto;
      font-size: 0;
    }

    &.purchase-service-header--app {
      .btn-right {
        right: 16px;
      }
    }
  }

  .purchase-service-tabs {
    height: 44px;
    padding: 0 15px;
    border-bottom: 1px solid #ebebeb;
    justify-content: space-between;

    .tab-button {
      flex: 1;
      max-width: none;
      padding: 6px 0;
      font-size: 16px;
      font-weight: 500;
      line-height: 30px;
    }
  }

  .mypage-menu-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 998;
  }

  .mypage-menu-dropdown {
    display: block;
    position: fixed;
    top: 60px;
    right: 16px;
    width: 280px;
    max-height: calc(100vh - 76px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    .login-msg-box {
      position: relative;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;

      .btn-home {
        position: absolute;
        top: 14px;
        right: 14px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        padding: 0 8px;
        border: 1px solid #e6e6e6;
        border-radius: 14px;
        background: #fff;
        color: #333;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;

        .icon-home {
          width: 14px;
          height: 14px;
          background-color: currentColor;
          -webkit-mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
          mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
        }

        &.active {
          color: #eb4c10;
          border-color: #f7d1c3;
          background: #fff5f2;
        }
      }

      .txt-box {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        color: #333;
        font-size: 15px;
        margin-bottom: 16px;
        padding-top: 6px;

        .txt1 {
          font-weight: 700;
        }

        .txt2 {
          color: #757575;
          font-size: 13px;
          margin-top: 4px;
        }
      }

      .gnb-menu-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;
        padding-top: 12px;
        border-top: 1px solid #f5f5f5;

        .gnb-menu-item {
          display: inline-block;
          padding: 6px 12px;
          color: #333;
          font-family: Pretendard;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
          text-decoration: none;
          border-radius: 6px;
          background: #f9f9f9;
          transition: all 0.2s;
          white-space: nowrap;

          &:hover {
            background: #f0f0f0;
            color: #eb4c10;
          }

          &.active {
            background: #fff5f2;
            color: #eb4c10;
            font-weight: 600;
          }
        }
      }

      .btn-xs.line {
        width: 100%;
        margin-top: 8px;
        padding: 8px 16px;
        border: 1px solid #ebebeb;
        border-radius: 6px;
        background: #fff;
        color: #333;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: #f5f5f5;
        }

        &.login-btn {
          background: #eb4c10;
          color: #fff;
          border-color: #eb4c10;

          &:hover {
            background: #d43d0e;
            border-color: #d43d0e;
          }
        }
      }
    }
  }
}

@media (min-width: 500px) and (max-width: 768px) {
  .gift-box-wrap {
    :deep(.gift-list) {
      .gift-card {
        flex: 0 0 calc((100% - 20px) / 2);
        max-width: calc((100% - 20px) / 2);
      }
    }
  }
}
</style>
